import React, { useEffect, useState } from "react";
import Layout from "../layout/layout";
import "../styles/dashboard.css";
import { ProgressBar, Spinner } from "react-bootstrap";
import Flag from "react-world-flags";
import countryList from "country-list";
import PieChart from "components/pieChart";
import { retrieveUserData } from "redux/authSlice";
import {
  useGetAllSubscriptionSummaryQuery,
  useGetCountriesPercentageQuery,
  useGetPerformingCountriesQuery,
} from "api/dashboardApi";
import {
  CountriesPercentageModel,
  PerformingCountriesModel,
  SubscriptionSummaryTotal,
} from "model/dashboardModel";
import { showToastError } from "components/toastify/ToastNotifications";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useGetPermissionsByIdQuery } from "api/userManagementApi";
import { CgArrowBottomLeft, CgArrowTopRight } from "react-icons/cg";
import { GrView } from "react-icons/gr";
import { SiSpotify } from "react-icons/si";
import { FaBuilding } from "react-icons/fa";
import ReactApexChart from "react-apexcharts";
import { FloatingLabel, Form } from "react-bootstrap";
import { currencyData } from "model/enums";
import { splitWord } from "utility/wordSeparator";
import { CiWarning } from "react-icons/ci";

type Props = {};
type MetricCardProps = {
  title: string;
  value: string;
  change: string;
  isPositive: boolean;
  borderColor: string;
};

const MetricCard: React.FC<MetricCardProps> = ({
  title,
  value,
  change,
  isPositive,
  borderColor,
}) => {
  // Map Tailwind border classes to actual color values
  const getBorderColorValue = (borderClass: string) => {
    const colorMap: { [key: string]: string } = {
      "border-orange-500": "#f97316",
      "border-blue-500": "#3b82f6",
      "border-purple-500": "#a855f7",
      "border-red-500": "#ef4444",
      "border-pink-500": "#ec4899",
      "border-green-500": "#22c55e",
      "border-yellow-500": "#eab308",
      "border-indigo-500": "#6366f1",
      "border-teal-500": "#14b8a6",
      "border-cyan-500": "#06b6d4",
    };

    return colorMap[borderClass] || "#6b7280"; // fallback to gray
  };

  const actualBorderColor = getBorderColorValue(borderColor);

  return (
    <div
      className={`metric-card-enhanced`}
      style={{
        position: "relative",
        background: "linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)",
        borderRadius: "16px",
        padding: "24px",
        boxShadow:
          "0 4px 20px rgba(0, 0, 0, 0.08), 0 1px 3px rgba(0, 0, 0, 0.05)",
        border: "1px solid rgba(226, 232, 240, 0.8)",
        borderLeft: `6px solid ${actualBorderColor}`,
        transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
        overflow: "hidden",
        minHeight: "140px",
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.transform = "translateY(-2px)";
        e.currentTarget.style.boxShadow =
          "0 8px 30px rgba(0, 0, 0, 0.12), 0 4px 6px rgba(0, 0, 0, 0.06)";
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = "translateY(0)";
        e.currentTarget.style.boxShadow =
          "0 4px 20px rgba(0, 0, 0, 0.08), 0 1px 3px rgba(0, 0, 0, 0.05)";
      }}
    >
      {/* Background decoration */}
      <div
        style={{
          position: "absolute",
          top: "-50%",
          right: "-20%",
          width: "120px",
          height: "120px",
          background: `linear-gradient(135deg, ${actualBorderColor}, transparent)`,
          borderRadius: "50%",
          opacity: 0.05,
          pointerEvents: "none",
        }}
      />

      {/* Content */}
      <div style={{ position: "relative", zIndex: 1 }}>
        <p
          style={{
            fontSize: "13px",
            fontWeight: "600",
            color: "#64748b",
            margin: "0 0 8px 0",
            textTransform: "uppercase",
            letterSpacing: "0.5px",
            lineHeight: "1.2",
          }}
        >
          {title}
        </p>

        <h2
          style={{
            fontSize: "32px",
            fontWeight: "700",
            color: "#1e293b",
            margin: "0 0 12px 0",
            lineHeight: "1.1",
            fontFamily: "system-ui, -apple-system, sans-serif",
          }}
        >
          {value}
        </h2>

        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "6px",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: "20px",
              height: "20px",
              borderRadius: "50%",
              backgroundColor: isPositive ? "#dcfce7" : "#fef2f2",
            }}
          >
            {isPositive ? (
              <CgArrowTopRight
                style={{
                  color: "#16a34a",
                  fontSize: "14px",
                  fontWeight: "bold",
                }}
              />
            ) : (
              <CgArrowBottomLeft
                style={{
                  color: "#dc2626",
                  fontSize: "14px",
                  fontWeight: "bold",
                }}
              />
            )}
          </div>

          <span
            style={{
              fontSize: "13px",
              fontWeight: "600",
              color: isPositive ? "#16a34a" : "#dc2626",
            }}
          >
            {change} from last month
          </span>
        </div>
      </div>
    </div>
  );
};

const MarginAlert = () => {
  return (
    <div className="marginAlert">
      <CiWarning className="marginAlertIconText" />

      <span className="marginAlertText">
        Margin Alert: 1 revenue line(s) below 60% margin threshold:
        Appliance/HaaS
      </span>
    </div>
  );
};

const RevenueTrendsChart = () => {
  const chartData = {
    series: [
      {
        name: "Revenue",
        data: [
          193000, 195000, 196000, 201000, 205000, 210000, 215000, 208000,
          218000, 216000, 220000, 260000,
        ],
      },
      {
        name: "Costs",
        data: [
          65000, 68000, 70000, 72000, 71000, 74000, 76000, 70000, 66000, 69000,
          71000, 77600,
        ],
      },
      {
        name: "Profit",
        data: [
          128000, 127000, 126000, 129000, 134000, 136000, 139000, 138000,
          138000, 137000, 142000, 177400,
        ],
      },
    ],
    options: {
      chart: {
        type: "line" as const,
        height: 400,
        toolbar: {
          show: false,
        },
        background: "transparent",
        fontFamily: "system-ui, -apple-system, sans-serif",
      },
      grid: {
        show: true,
        borderColor: "#E5E7EB",
        strokeDashArray: 3,
        xaxis: {
          lines: {
            show: true,
          },
        },
        yaxis: {
          lines: {
            show: true,
          },
        },
        padding: {
          top: 20,
          right: 30,
          bottom: 10,
          left: 20,
        },
      },
      stroke: {
        curve: "smooth" as const, // Explicitly type as const
        width: 3,
      },
      markers: {
        size: 6,
        colors: ["#2196F3", "#F44336", "#4CAF50"],
        strokeColors: "#fff",
        strokeWidth: 2,
        hover: {
          size: 8,
        },
      },
      xaxis: {
        categories: [
          "Jan",
          "Feb",
          "Mar",
          "Apr",
          "May",
          "Jun",
          "Jul",
          "Aug",
          "Sept",
          "Oct",
          "Nov",
          "Dec",
        ],
        axisBorder: {
          show: false,
        },
        axisTicks: {
          show: false,
        },
        labels: {
          style: {
            colors: "#6B7280",
            fontSize: "14px",
            fontWeight: "500",
          },
        },
      },
      yaxis: {
        min: 0,
        max: 260000,
        tickAmount: 4,
        labels: {
          formatter: function (value: number) {
            return value.toLocaleString();
          },
          style: {
            colors: "#6B7280",
            fontSize: "14px",
            fontWeight: "500",
          },
        },
      },
      legend: {
        position: "bottom" as const,
        horizontalAlign: "left" as const,
        offsetY: 10,
        markers: {
          width: 12,
          height: 12,
          radius: 2,
        },
        labels: {
          colors: "#374151",
          useSeriesColors: false,
        },
        itemMargin: {
          horizontal: 20,
          vertical: 5,
        },
      },
      tooltip: {
        shared: true,
        intersect: false,
        style: {
          fontSize: "14px",
        },
        y: {
          formatter: function (value: number) {
            return "€" + value.toLocaleString();
          },
        },
      },
      colors: ["#2196F3", "#F44336", "#4CAF50"],
    },
  };

  return (
    <div className="revenue-trends-container">
      <div
        style={{
          backgroundColor: "#ffffff",
          borderRadius: "12px",
          padding: "24px",
          boxShadow:
            "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)",
          border: "1px solid #E5E7EB",
        }}
      >
        <div className="chart-header" style={{ marginBottom: "24px" }}>
          <h3
            style={{
              fontSize: "24px",
              fontWeight: "700",
              color: "#1F2937",
              margin: "0 0 8px 0",
              fontFamily: "system-ui, -apple-system, sans-serif",
            }}
          >
            Revenue Trends
          </h3>
          <p
            style={{
              fontSize: "16px",
              color: "#6B7280",
              margin: "0",
              fontWeight: "400",
            }}
          >
            Revenue, costs, and profit over time in EUR
          </p>
        </div>

        <ReactApexChart
          options={chartData.options}
          series={chartData.series}
          type="line"
          height={400}
        />
      </div>
    </div>
  );
};

const Dashboard: React.FC = (props: Props) => {
  // const navigate = useNavigate();
  const [getPackageName] = useSearchParams();
  const packageName = getPackageName.get("package");
  const menuName = getPackageName.get("menu");
  const durationName = getPackageName.get("duration");
  const menuSplit = splitWord(menuName || "");

  const userRoleId = retrieveUserData()?.roleId || "";

  const {
    data: permissionsData,
    // isFetching: fetchingPermission,
    refetch: refetchUserPermission,
  } = useGetPermissionsByIdQuery(userRoleId, {
    skip: userRoleId ? false : true,
  });
  const userPermission =
    permissionsData?.data &&
    permissionsData?.data?.permissions?.includes("View Dashboard");

  useEffect(() => {
    if (permissionsData?.data) {
      refetchUserPermission();
    }
  }, []);

  const [loading, setLoading] = useState(false);
  const [selectedApplication, setSelectedApplication] = useState("Joble");
  const [subSummary, setSubSummary] = useState<SubscriptionSummaryTotal[]>();
  const [product, setProduct] = useState<SubscriptionSummaryTotal | null>();
  const [searchColumn, setSearchColumn] = useState("col-md-4");
  const [menuSearch, setMenuSearch] = useState(menuSplit || "");
  const [subMenuSearch, setSubMenuSearch] = useState(durationName || "");
  const [selectedPackage, setSelectedPackage] = useState(packageName || "");
  const [startEndDate, setStartEndDate] = useState({
    startDate: "",
    endDate: "",
  });
  const [subInnerMenuSearch, setSubInnerMenuSearch] = useState("");
  const [searchResult, setSearchResult] = useState(false);

  const [performanceCountries, setPerformanceCountries] = useState<
    PerformingCountriesModel[]
  >([]);
  const [countriesPercentage, setCountriesPercentage] = useState<
    CountriesPercentageModel[]
  >([]);

  const {
    data: getSubSummary,
    error: getSubSummaryError,
    isFetching: getSubSummaryisFetching,
  } = useGetAllSubscriptionSummaryQuery();
  const {
    data: getPerformanceCountries,
    error: getPerformanceCountriesError,
    isFetching: getPerformanceCountriesisFetching,
  } = useGetPerformingCountriesQuery();
  const {
    data: getCountriesPercentage,
    error: getCountriesPercentageError,
    isFetching: getCountriesPercentageisFetching,
  } = useGetCountriesPercentageQuery();

  useEffect(() => {
    if (getSubSummaryisFetching) {
      setLoading(true);
    } else {
      setLoading(false);
      if (getSubSummaryError) {
        showToastError(
          getSubSummary?.ResponseMessage ||
            "Unable to fetch data from the server"
        );
      } else {
        setSubSummary(getSubSummary?.data as SubscriptionSummaryTotal[]);
        setProduct(getSubSummary?.data ? getSubSummary?.data[0] : null);
      }
    }
  }, [getSubSummaryisFetching, selectedApplication]);

  useEffect(() => {
    if (getCountriesPercentageisFetching) {
      setLoading(true);
    } else {
      setLoading(false);
      if (getPerformanceCountriesError) {
        showToastError(
          getCountriesPercentage?.ResponseMessage ||
            "Unable to fetch data from the server"
        );
      } else {
        setCountriesPercentage(
          getCountriesPercentage?.data as CountriesPercentageModel[]
        );
      }
    }
  }, [getCountriesPercentageisFetching, selectedApplication]);

  useEffect(() => {
    if (getPerformanceCountriesisFetching) {
      setLoading(true);
    } else {
      setLoading(false);
      if (getCountriesPercentageError) {
        showToastError(
          getPerformanceCountries?.ResponseMessage ||
            "Unable to fetch data from the server"
        );
      } else {
        setPerformanceCountries(
          getPerformanceCountries?.data as PerformingCountriesModel[]
        );
      }
    }
  }, [getPerformanceCountriesisFetching, selectedApplication]);

  
  

  const donutChart = {
    options: {
      labels: performanceCountries?.map((item) => item.country || ""),
      legend: { show: false },
      tooltip: {
        enabled: true,
      },
      dataLabels: {
        enabled: false,
      },
      colors: [
        "#008CFE",
        "#03256C",
        "#854C27",
        "#DD7230",
        "#FCAB10",
        "#44AF69",
      ],
    },
    series: performanceCountries?.map((item) => Number(item.tenantCount)) || [],
    type: "donut" as const,
    width: 180,
    height: 180,
  };

  const revenueLinesData = [
    {
      revenueLine: "R1 - legal number",
      product: "Chat, Tasks, Meetings, Voice, Wiki",
      revenue: "€125,000",
      cost: "€25,000",
      profit: "€100,000",
      margin: "80%",
      metrics: "1250 Seats",
      growth: "7.45%",
      growthIcon: <CgArrowTopRight className="text-success" />,
      action: <GrView className="text-success" />,
      marginColor: "#28a745",
    },
    {
      revenueLine: "R2 - AI Usage",
      product: "Sophia Voice Agent",
      revenue: "€28,000",
      cost: "€8,400",
      profit: "€19,600",
      margin: "80%",
      metrics: "56,000 Minutes, 1400 Bookings",
      growth: "7.45%",
      growthIcon: <CgArrowTopRight className="text-success" />,
      action: <GrView className="text-success" />,
      marginColor: "#ffc107",
    },
    {
      revenueLine: "R2 - AI Usage",
      product: "Luna Wiki AI",
      revenue: "€17,000",
      cost: "€5,950",
      profit: "€11,050",
      margin: "65%",
      metrics: "0",
      growth: "7.45%",
      growthIcon: <CgArrowTopRight className="text-success" />,
      action: <GrView className="text-success" />,
      marginColor: "#fd7e14",
    },
    {
      revenueLine: "R3 – Appliance / Haas",
      product: "Plug-and-play on-prem server",
      revenue: "€85,000",
      cost: "€38,250",
      profit: "€46,750",
      margin: "55%",
      metrics: "85 Hardware",
      growth: "7.45%",
      growthIcon: <CgArrowTopRight className="text-success" />,
      action: <GrView className="text-success" />,
      marginColor: "#F2994A33",
    },
  ];

  const clientsData = [
    {
      companyName: "Zarttech Technologies",
      country: "Netherlands",
      countryFlag: "NL",
      staffSize: "50 - 200",
      productUsed: "Sophia Voice Agent",
      seats: 0,
      minutes: "7,000",
      bookings: 160,
      hardware: 160,
      cost: "€4,000",
      revenue: "€20,000",
      icon: (
        <div
          className="company-icon"
          style={{
            backgroundColor: "#FF6B35",
            color: "white",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            width: "40px",
            height: "40px",
            borderRadius: "8px",
            fontSize: "12px",
            fontWeight: "bold",
          }}
        >
          ZT
        </div>
      ),
    },
    {
      companyName: "Spotify Music",
      country: "United Kingdom",
      countryFlag: "GB",
      staffSize: "50 - 200",
      productUsed: "SaaS seats",
      seats: 150,
      minutes: "7,000",
      bookings: 120,
      hardware: 120,
      cost: "€4,000",
      revenue: "€15,000",
      icon: (
        <div
          style={{
            backgroundColor: "#1DB954",
            color: "white",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            width: "40px",
            height: "40px",
            borderRadius: "50%",
          }}
        >
          <SiSpotify size={20} />
        </div>
      ),
    },
    {
      companyName: "Chase Bank Limited",
      country: "France",
      countryFlag: "FR",
      staffSize: "50 - 200",
      productUsed: "SaaS seats",
      seats: 150,
      minutes: "0",
      bookings: 0,
      hardware: 0,
      cost: "€4,000",
      revenue: "€10,000",
      icon: (
        <div
          style={{
            backgroundColor: "#0066B2",
            color: "white",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            width: "40px",
            height: "40px",
            borderRadius: "50%",
          }}
        >
          <FaBuilding size={18} />
        </div>
      ),
    },
    {
      companyName: "Spotify Music",
      country: "Portugal",
      countryFlag: "PT",
      staffSize: "50 - 200",
      productUsed: "Luna Wiki AI",
      seats: 100,
      minutes: "0",
      bookings: 0,
      hardware: 0,
      cost: "€4,000",
      revenue: "€10,000",
      icon: (
        <div
          style={{
            backgroundColor: "#1DB954",
            color: "white",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            width: "40px",
            height: "40px",
            borderRadius: "50%",
          }}
        >
          <SiSpotify size={20} />
        </div>
      ),
    },
  ];

  // const handleSearchResult = () => {
  //   setSearchResult(true);
  // };

  return (
    <Layout>
      <div>
        {loading ? (
          <div className="p-5 text-center">
            <Spinner />
          </div>
        ) : (
          <div className="mt-4">
            <div>
              <h5 className="">Financial & Revenue Dashboard</h5>
              <p>
                Track multi-currency revenue, costs, and profit across all
                revenue lines
              </p>
            </div>
            <Form>
              <div className="row">
                <div className="col-lg-11 row">
                  {/* <div className={searchColumn}>
                      <FloatingLabel controlId="floatingSelect" label="Select a Package" className='float-input'>
                          <Form.Select aria-label="Floating label select example" defaultValue={selectedPackage} onChange={(e) => setSelectedPackage(e.target.value)} className="border-0 w-100">
                              <option value=''>Choose a Package</option>
                              {productData?.map((item, key) => <option key={key} value={item?.name}>{item?.name}</option>)}
                          </Form.Select>
                      </FloatingLabel>
                  </div> */}
                  <div className={searchColumn}>
                    <FloatingLabel
                      controlId="floatingSelect"
                      label="Choose Region"
                      className="float-input"
                    >
                      <Form.Select
                        defaultValue={menuSearch}
                        onChange={(e) => setMenuSearch(e.target.value)}
                        aria-label="Floating label select example"
                        className="border-0 w-100"
                      >
                        <option value="">Choose Region</option>
                        {currencyData
                          ?.filter((item) => item?.name === selectedPackage)[0]
                          ?.data?.map((item, key) => (
                            <option key={key} value={item?.name}>
                              {item?.name}
                            </option>
                          ))}
                      </Form.Select>
                    </FloatingLabel>
                  </div>
                  {menuSearch === "Product Update" ||
                  menuSearch === "Waitlist" ||
                  menuSearch === "App Usage Frequency" ? (
                    ""
                  ) : (
                    <>
                      {menuSearch === "General Summary" ||
                      menuSearch === "Subscription Summary" ||
                      menuSearch === "Call Count" ||
                      menuSearch === "Total Timesheet" ||
                      menuSearch === "Project KPI Settings" ||
                      menuSearch === "Joble Wallet" ||
                      menuSearch === "Rule Engine" ||
                      menuSearch === "JobPays Wallet" ||
                      menuSearch === "" ? (
                        <div
                          className={`${searchColumn} ${
                            subMenuSearch === "Custom" ? "d-flex gap-3" : ""
                          }`}
                        >
                          <FloatingLabel
                            controlId="floatingSelect"
                            label={
                              menuSearch === "Rule Engine"
                                ? "Rule Type"
                                : "Choose Duration"
                            }
                            className="float-input"
                          >
                            <Form.Select
                              defaultValue={subMenuSearch}
                              onChange={(e) => setSubMenuSearch(e.target.value)}
                              aria-label="Floating label select example"
                              className={`border-0 w-100`}
                            >
                              <option value="">
                                {menuSearch === "Rule Engine"
                                  ? "Choose Type"
                                  : "Choose Duration"}
                              </option>
                              {currencyData
                                ?.filter(
                                  (item) => item?.name === selectedPackage
                                )[0]
                                ?.data?.filter(
                                  (item) => item?.name === menuSearch
                                )[0]
                                ?.subData?.map((item, key) => (
                                  <option key={key} value={item}>
                                    {splitWord(item)}
                                  </option>
                                ))}
                            </Form.Select>
                          </FloatingLabel>
                          {subMenuSearch === "Custom" && (
                            <div className="d-flex gap-1 w-50">
                              <FloatingLabel
                                controlId="floatingSelect"
                                label="Start Date"
                                className="float-input w-50"
                              >
                                <Form.Control
                                  type="date"
                                  onChange={(e) =>
                                    setStartEndDate({
                                      ...startEndDate,
                                      startDate: e.target.value,
                                    })
                                  }
                                />
                              </FloatingLabel>
                              <FloatingLabel
                                controlId="floatingSelect"
                                label="End Date"
                                className="float-input w-50"
                              >
                                <Form.Control
                                  type="date"
                                  onChange={(e) =>
                                    setStartEndDate({
                                      ...startEndDate,
                                      endDate: e.target.value,
                                    })
                                  }
                                />
                              </FloatingLabel>
                            </div>
                          )}
                        </div>
                      ) : (
                        <>
                          <div className={searchColumn}>
                            <FloatingLabel
                              controlId="floatingSelect"
                              label="Works with selects"
                              className="float-input"
                            >
                              <Form.Select
                                onChange={(e) =>
                                  setSubMenuSearch(e.target.value)
                                }
                                aria-label="Floating label select example"
                                className="border-0 w-100"
                              >
                                <option value="">Choose Duration</option>
                                {currencyData
                                  ?.filter(
                                    (item) => item?.name === selectedPackage
                                  )[0]
                                  ?.data?.filter(
                                    (item) => item?.name === menuSearch
                                  )[0]
                                  ?.subData?.map((item, key) => (
                                    <option key={key} value={item}>
                                      {splitWord(item)}
                                    </option>
                                  ))}
                              </Form.Select>
                            </FloatingLabel>
                          </div>
                          <div className={searchColumn}>
                            <FloatingLabel
                              controlId="floatingSelect"
                              label="Select Company"
                              className="float-input"
                            >
                              <Form.Select
                                onChange={(e) =>
                                  setSubInnerMenuSearch(e.target.value)
                                }
                                aria-label="Floating label select example"
                                className="border-0 w-100"
                              >
                                <option value="">Choose Company</option>
                                <option>Zarttech Technologies</option>
                              </Form.Select>
                            </FloatingLabel>
                          </div>
                        </>
                      )}
                    </>
                  )}
                </div>
                {/* <div className="col-lg-1">
                  <button
                    type="button"
                    onClick={() => handleSearchResult()}
                    className="btn-primary"
                  >
                    View
                  </button>
                </div> */}
              </div>
            </Form>
            <MarginAlert />
            <div className="metrics-grid">
              <MetricCard
                title="Monthly Recurring Revenue"
                value="€125,000"
                change="+20%"
                isPositive={true}
                borderColor="border-orange-500"
              />
              <MetricCard
                title="AI Usage Revenue"
                value="€40,000"
                change="+20%"
                isPositive={true}
                borderColor="border-blue-500"
              />
              <MetricCard
                title="Appliance Revenue"
                value="€85,000"
                change="+20%"
                isPositive={true}
                borderColor="border-purple-500"
              />
              <MetricCard
                title="Total Costs"
                value="€77,600"
                change="-3.2%"
                isPositive={false}
                borderColor="border-red-500"
              />
              <MetricCard
                title="Net Profit"
                value="€177,400"
                change="+20%"
                isPositive={true}
                borderColor="border-pink-500"
              />
            </div>
            <RevenueTrendsChart />
            <div className="row">
              <div className="col-lg-6">
                <div className="card-default">
                  <h6 className="fw-bold">Top Countries</h6>
                  <div className="row">
                    {countriesPercentage?.length > 0 ? (
                      countriesPercentage?.map((item, key) => (
                        <div key={key} className="col-lg-6 h-100">
                          <p className="dark-text mt-2 mb-1">
                            {item?.country && (
                              <Flag
                                code={countryList?.getCode(item?.country)}
                                name={item?.country}
                                width={20}
                              />
                            )}
                            &nbsp; {item?.country}
                          </p>
                          <div className="text-end">
                            <ProgressBar
                              style={{ width: "85%" }}
                              now={item?.percentage}
                            />
                            <p className="percent-number p-0 ms-2">
                              {parseFloat(item?.percentage?.toFixed(0))}%
                            </p>
                          </div>
                        </div>
                      ))
                    ) : (
                      <div className="col-lg-12">
                        {" "}
                        <span>No data available</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div className="col-lg-6">
                <div className="card-default py-2 px-4">
                  <div className="row">
                    <div className="col-lg-3 p-0 me-auto">
                      <div className="position-absolute font-size-smm text-center my-5 mx-5 px-3 py-2">
                        <span className="pt-5">
                          Most used <br /> Products
                        </span>
                      </div>
                      <PieChart {...donutChart} />
                    </div>
                    <div className="col-lg-9 py-3 row">
                      {/* <h6 className='fw-bold'>Top Countries Performances</h6> */}
                      <div className="col-lg-6 mb-2 font-size-smm">
                        <p className="py-1 my-1">
                          <span className="bg-primary badge-circle">
                            &nbsp;&nbsp;&nbsp;&nbsp;
                          </span>{" "}
                          <span className="px-1">#1. Dashboard</span>
                          <span className="px-1">-</span>{" "}
                          <span className="px-0">28%</span>{" "}
                          <span className="text-success">
                            <CgArrowTopRight />
                            45%
                          </span>
                        </p>
                        <p className="py-1 my-1">
                          <span className="bg-danger badge-circle">
                            &nbsp;&nbsp;&nbsp;&nbsp;
                          </span>
                          <span className="px-1">#1. Chat</span>
                          <span className="px-1">-</span>{" "}
                          <span className="px-0">62%</span>
                          <span className="text-danger">
                            <CgArrowBottomLeft />
                            11%
                          </span>
                        </p>
                        <p className="py-1 my-1">
                          <span className="bg-warning badge-circle">
                            &nbsp;&nbsp;&nbsp;&nbsp;
                          </span>{" "}
                          <span className="px-1">#2. Meeting</span>
                          <span className="px-1">-</span>{" "}
                          <span className="px-0">18%</span>
                          <span className="text-success">
                            <CgArrowTopRight />
                            9%
                          </span>
                        </p>
                        <p className="py-1 my-1">
                          <span className="bg-info badge-circle">
                            &nbsp;&nbsp;&nbsp;&nbsp;
                          </span>
                          <span className="px-1">#3. Todo</span>
                          <span className="px-1">-</span>
                          <span className="px-0">32%</span>{" "}
                          <span className="text-danger">
                            <CgArrowBottomLeft />
                            16%
                          </span>
                        </p>
                      </div>
                      <div className="col-lg-6 mb-2 font-size-smm">
                        <p className="py-1 my-1">
                          <span className="bg-primary badge-circle">
                            &nbsp;&nbsp;&nbsp;&nbsp;
                          </span>{" "}
                          <span className="px-1">#1. Dashboard</span>
                          <span className="px-1">-</span>{" "}
                          <span className="px-0">28%</span>{" "}
                          <span className="text-success">
                            <CgArrowTopRight />
                            45%
                          </span>
                        </p>
                        <p className="py-1 my-1">
                          <span className="bg-danger badge-circle">
                            &nbsp;&nbsp;&nbsp;&nbsp;
                          </span>
                          <span className="px-1">#1. Chat</span>
                          <span className="px-1">-</span>{" "}
                          <span className="px-0">62%</span>
                          <span className="text-danger">
                            <CgArrowBottomLeft />
                            11%
                          </span>
                        </p>
                        <p className="py-1 my-1">
                          <span className="bg-warning badge-circle">
                            &nbsp;&nbsp;&nbsp;&nbsp;
                          </span>{" "}
                          <span className="px-1">#2. Meeting</span>
                          <span className="px-1">-</span>{" "}
                          <span className="px-0">18%</span>
                          <span className="text-success">
                            <CgArrowTopRight />
                            9%
                          </span>
                        </p>
                        <p className="py-1 my-1">
                          <span className="bg-info badge-circle">
                            &nbsp;&nbsp;&nbsp;&nbsp;
                          </span>
                          <span className="px-1">#3. Todo</span>
                          <span className="px-1">-</span>
                          <span className="px-0">32%</span>{" "}
                          <span className="text-danger">
                            <CgArrowBottomLeft />
                            16%
                          </span>
                        </p>
                      </div>
                      {/* {performanceCountries?.length > 0 ? performanceCountries?.map((item, key) => <div className='col-lg-6 mb-2'>
                        <span>#{key + 1}. &nbsp;
                            {item?.country && <Flag code={countryList?.getCode(item?.country)} name={item?.country} width={20} />} &nbsp;
                            {item?.country} -  {item?.tenantCount}</span>
                    </div>) : <div className='col-lg-6 mb-2'>No data available</div>} */}
                    </div>
                  </div>
                </div>
                <div className="row">
                  {/* <div className="col-lg-6">
                    <div className={`card-1`}>
                      <div className="d-flex justify-content-between">
                        <h6 className="fw-bold">{product?.applicationName}</h6>
                      </div>
                      <p className="dark-text mt-2 mb-1">Subscription Count</p>
                      <div className="d-flex justify-content-between second-font">
                        <h2>{product?.totalSubscriptions || 0}</h2>
                        <GraphIcon fillColor="#EB5757" />
                      </div>
                      <div className="d-flex justify-content-between mt-2">
                        <p className="dark-text">No of Company Users:</p>
                        <h6 className="fw-bold">
                          {product?.totalCompanies || 0}
                        </h6>
                      </div>
                      <div className="d-flex justify-content-between">
                        <p className="dark-text">Pending Request:</p>
                        <h6 className="fw-bold">
                          {product?.pendingRequest || 0}
                        </h6>
                      </div>
                      <div className="d-flex justify-content-between">
                        <p className="dark-text">Total Revenue:</p>
                        <h6 className="fw-bold">
                          €{product?.totalRevenue.toLocaleString() || 0}
                        </h6>
                      </div>
                      <div className="d-flex justify-content-between border-top pt-3">
                        <button className=''>Quick look</button>
                        <button
                          type="button"
                          onClick={() =>
                            navigate(
                              `/subscription?package=${product?.applicationName}&menu=SubscriptionSummary&duration=OneMonth`
                            )
                          }
                          className="w-100"
                        >
                          Manage
                        </button>
                      </div>
                    </div>
                  </div> */}
                </div>
              </div>
            </div>

            <div className="table-default mt-4">
              <h6 className="fw-bold mb-3">Revenue Lines Performance</h6>
              <p
                className="text-muted small mb-4"
                style={{ fontSize: "14px", color: "#9ca3af" }}
              >
                Detailed breakdown with subtotals for seats, AI minutes,
                bookings, and hardware
              </p>

              <div className="table-responsive">
                <table className="revenue-lines-table">
                  <thead>
                    <tr>
                      <th
                        style={{
                          color: "#9ca3af",
                          fontWeight: "500",
                          fontSize: "14px",
                          padding: "16px 20px",
                          borderBottom: "none",
                          backgroundColor: "transparent",
                        }}
                      >
                        Revenue Line
                      </th>
                      <th
                        style={{
                          color: "#9ca3af",
                          fontWeight: "500",
                          fontSize: "14px",
                          padding: "16px 20px",
                          borderBottom: "none",
                          backgroundColor: "transparent",
                        }}
                      >
                        Product
                      </th>
                      <th
                        style={{
                          color: "#9ca3af",
                          fontWeight: "500",
                          fontSize: "14px",
                          padding: "16px 20px",
                          borderBottom: "none",
                          backgroundColor: "transparent",
                        }}
                      >
                        Revenue
                      </th>
                      <th
                        style={{
                          color: "#9ca3af",
                          fontWeight: "500",
                          fontSize: "14px",
                          padding: "16px 20px",
                          borderBottom: "none",
                          backgroundColor: "transparent",
                        }}
                      >
                        Cost
                      </th>
                      <th
                        style={{
                          color: "#9ca3af",
                          fontWeight: "500",
                          fontSize: "14px",
                          padding: "16px 20px",
                          borderBottom: "none",
                          backgroundColor: "transparent",
                        }}
                      >
                        Profit
                      </th>
                      <th
                        style={{
                          color: "#9ca3af",
                          fontWeight: "500",
                          fontSize: "14px",
                          padding: "16px 20px",
                          borderBottom: "none",
                          backgroundColor: "transparent",
                        }}
                      >
                        Margin
                      </th>
                      <th
                        style={{
                          color: "#9ca3af",
                          fontWeight: "500",
                          fontSize: "14px",
                          padding: "16px 20px",
                          borderBottom: "none",
                          backgroundColor: "transparent",
                        }}
                      >
                        Metrics
                      </th>
                      <th
                        style={{
                          color: "#9ca3af",
                          fontWeight: "500",
                          fontSize: "14px",
                          padding: "16px 20px",
                          borderBottom: "none",
                          backgroundColor: "transparent",
                        }}
                      >
                        Growth
                      </th>
                      <th
                        style={{
                          color: "#9ca3af",
                          fontWeight: "500",
                          fontSize: "14px",
                          padding: "16px 20px",
                          borderBottom: "none",
                          backgroundColor: "transparent",
                        }}
                      >
                        Action
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {revenueLinesData.map((item, index) => (
                      <tr
                        key={index}
                        style={{
                          borderBottom:
                            index < revenueLinesData.length - 1
                              ? "1px solid #f0f0f0"
                              : "none",
                        }}
                      >
                        <td
                          style={{ padding: "20px", verticalAlign: "middle" }}
                        >
                          <span
                            style={{
                              fontWeight: "600",
                              color: "#1f2937",
                              fontSize: "14px",
                            }}
                          >
                            {item.revenueLine}
                          </span>
                        </td>
                        <td
                          style={{
                            padding: "20px",
                            verticalAlign: "middle",
                            color: "#6b7280",
                            fontSize: "14px",
                            maxWidth: "200px",
                          }}
                        >
                          {item.product}
                        </td>
                        <td
                          style={{
                            padding: "20px",
                            verticalAlign: "middle",
                            color: "#1f2937",
                            fontSize: "14px",
                            fontWeight: "500",
                          }}
                        >
                          {item.revenue}
                        </td>
                        <td
                          style={{
                            padding: "20px",
                            verticalAlign: "middle",
                            color: "#6b7280",
                            fontSize: "14px",
                          }}
                        >
                          {item.cost}
                        </td>
                        <td
                          style={{
                            padding: "20px",
                            verticalAlign: "middle",
                            color: "#1f2937",
                            fontSize: "14px",
                            fontWeight: "500",
                          }}
                        >
                          {item.profit}
                        </td>
                        <td
                          style={{ padding: "20px", verticalAlign: "middle" }}
                        >
                          <span
                            style={{
                              backgroundColor: item.marginColor,
                              color: "white",
                              padding: "4px 12px",
                              borderRadius: "16px",
                              fontSize: "12px",
                              fontWeight: "600",
                            }}
                          >
                            {item.margin}
                          </span>
                        </td>
                        <td
                          style={{
                            padding: "20px",
                            verticalAlign: "middle",
                            color: "#6b7280",
                            fontSize: "14px",
                          }}
                        >
                          {item.metrics}
                        </td>
                        <td
                          style={{ padding: "20px", verticalAlign: "middle" }}
                        >
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: "4px",
                            }}
                          >
                            {item.growthIcon}
                            <span
                              style={{
                                color: "#059669",
                                fontSize: "14px",
                                fontWeight: "500",
                              }}
                            >
                              {item.growth}
                            </span>
                          </div>
                        </td>
                        <td
                          style={{
                            padding: "20px",
                            verticalAlign: "middle",
                            textAlign: "center",
                          }}
                        >
                          {item.action}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
            <div className="table-default mt-4">
              <h6 className="fw-bold mb-3">All Regions - Clients</h6>

              <div className="table-responsive">
                <table className="clients-table">
                  <thead>
                    <tr>
                      <th
                        style={{
                          color: "#6c757d",
                          fontWeight: "500",
                          fontSize: "14px",
                          padding: "16px 20px",
                          borderBottom: "none",
                        }}
                      >
                        Company Name
                      </th>
                      <th
                        style={{
                          color: "#6c757d",
                          fontWeight: "500",
                          fontSize: "14px",
                          padding: "16px 20px",
                          borderBottom: "none",
                        }}
                      >
                        Country
                      </th>
                      <th
                        style={{
                          color: "#6c757d",
                          fontWeight: "500",
                          fontSize: "14px",
                          padding: "16px 20px",
                          borderBottom: "none",
                        }}
                      >
                        Staff Size
                      </th>
                      <th
                        style={{
                          color: "#6c757d",
                          fontWeight: "500",
                          fontSize: "14px",
                          padding: "16px 20px",
                          borderBottom: "none",
                        }}
                      >
                        Product used
                      </th>
                      <th
                        style={{
                          color: "#6c757d",
                          fontWeight: "500",
                          fontSize: "14px",
                          padding: "16px 20px",
                          borderBottom: "none",
                        }}
                      >
                        Seats
                      </th>
                      <th
                        style={{
                          color: "#6c757d",
                          fontWeight: "500",
                          fontSize: "14px",
                          padding: "16px 20px",
                          borderBottom: "none",
                        }}
                      >
                        Minutes
                      </th>
                      <th
                        style={{
                          color: "#6c757d",
                          fontWeight: "500",
                          fontSize: "14px",
                          padding: "16px 20px",
                          borderBottom: "none",
                        }}
                      >
                        Bookings
                      </th>
                      <th
                        style={{
                          color: "#6c757d",
                          fontWeight: "500",
                          fontSize: "14px",
                          padding: "16px 20px",
                          borderBottom: "none",
                        }}
                      >
                        Hardware
                      </th>
                      <th
                        style={{
                          color: "#6c757d",
                          fontWeight: "500",
                          fontSize: "14px",
                          padding: "16px 20px",
                          borderBottom: "none",
                        }}
                      >
                        Cost
                      </th>
                      <th
                        style={{
                          color: "#6c757d",
                          fontWeight: "500",
                          fontSize: "14px",
                          padding: "16px 20px",
                          borderBottom: "none",
                        }}
                      >
                        Revenue
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {clientsData.map((item, index) => (
                      <tr
                        key={index}
                        style={{
                          borderBottom:
                            index < clientsData.length - 1
                              ? "1px solid #f0f0f0"
                              : "none",
                        }}
                      >
                        <td
                          style={{ padding: "20px", verticalAlign: "middle" }}
                        >
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: "12px",
                            }}
                          >
                            {item.icon}
                            <span
                              style={{
                                fontWeight: "600",
                                color: "#2c3e50",
                                fontSize: "14px",
                              }}
                            >
                              {item.companyName}
                            </span>
                          </div>
                        </td>
                        <td
                          style={{
                            padding: "20px",
                            verticalAlign: "middle",
                            color: "#495057",
                            fontSize: "14px",
                          }}
                        >
                          {item.country}
                        </td>
                        <td
                          style={{
                            padding: "20px",
                            verticalAlign: "middle",
                            color: "#495057",
                            fontSize: "14px",
                          }}
                        >
                          {item.staffSize}
                        </td>
                        <td
                          style={{ padding: "20px", verticalAlign: "middle" }}
                        >
                          <div
                            style={{
                              display: "flex",
                              flexDirection: "column",
                              gap: "2px",
                            }}
                          >
                            <span
                              style={{
                                fontWeight: "500",
                                color: "#2c3e50",
                                fontSize: "14px",
                              }}
                            >
                              {item.productUsed.split(" ")[0] === "SaaS"
                                ? "SaaS seats"
                                : item.productUsed.split(" ")[0] === "Sophia"
                                ? "Sophia Voice Agent"
                                : item.productUsed}
                            </span>
                            {item.productUsed === "SaaS seats" && (
                              <span
                                style={{ color: "#6c757d", fontSize: "12px" }}
                              >
                                Sophia Voice Agent
                              </span>
                            )}
                          </div>
                        </td>
                        <td
                          style={{
                            padding: "20px",
                            verticalAlign: "middle",
                            color: "#495057",
                            fontSize: "14px",
                            textAlign: "center",
                          }}
                        >
                          {item.seats}
                        </td>
                        <td
                          style={{
                            padding: "20px",
                            verticalAlign: "middle",
                            color: "#495057",
                            fontSize: "14px",
                            textAlign: "center",
                          }}
                        >
                          {item.minutes}
                        </td>
                        <td
                          style={{
                            padding: "20px",
                            verticalAlign: "middle",
                            color: "#495057",
                            fontSize: "14px",
                            textAlign: "center",
                          }}
                        >
                          {item.bookings}
                        </td>
                        <td
                          style={{
                            padding: "20px",
                            verticalAlign: "middle",
                            color: "#495057",
                            fontSize: "14px",
                            textAlign: "center",
                          }}
                        >
                          {item.hardware}
                        </td>
                        <td
                          style={{
                            padding: "20px",
                            verticalAlign: "middle",
                            color: "#495057",
                            fontSize: "14px",
                            fontWeight: "500",
                          }}
                        >
                          {item.cost}
                        </td>
                        <td
                          style={{
                            padding: "20px",
                            verticalAlign: "middle",
                            color: "#495057",
                            fontSize: "14px",
                            fontWeight: "600",
                          }}
                        >
                          {item.revenue}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              <div className="d-flex justify-content-between align-items-center mt-4">
                <div className="text-muted small">Showing 1 - 7 of 300</div>
                <div className="d-flex gap-2">
                  {[1, 2, 3, 4, 5].map((page) => (
                    <button
                      key={page}
                      className={`pagination-btn ${page === 1 ? "active" : ""}`}
                      style={{
                        width: "32px",
                        height: "32px",
                        border:
                          page === 1
                            ? "1px solid #007bff"
                            : "1px solid #dee2e6",
                        backgroundColor: page === 1 ? "#007bff" : "white",
                        color: page === 1 ? "white" : "#6c757d",
                        borderRadius: "4px",
                        fontSize: "14px",
                        cursor: "pointer",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      {page}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default Dashboard;

// {/* <div className="row">
//     {subSummary?.map((product, key) => <div key={key} className="col-lg-3">
//         <div className={`card-${key + 1}`}>
//             <div className='d-flex justify-content-between'>
//                 <h6 className='fw-bold'>{product?.applicationName}</h6>
//                 {/* <span className='notification-icon shake1'>
//                                     <IoIosNotificationsOutline size={30} />
//                                     <span className='badge bg-danger'>0</span></span> */}
//             </div>
//             <p className='dark-text mt-2 mb-1'>Subscription Count</p>
//             <div className='d-flex justify-content-between second-font'>
//                 <h2>{product?.totalSubscriptions || 0}</h2>
//                 <GraphIcon fillColor='#EB5757' />
//             </div>
//             <div className='d-flex justify-content-between mt-2'>
//                 <p className='dark-text'>No of Company Users:</p>
//                 <h6 className='fw-bold'>{product?.totalCompanies || 0}</h6>
//             </div>
//             {/* <div className='d-flex justify-content-between'>
//                                 <p className='dark-text'>Subscription Count:</p>
//                                 <h6 className='fw-bold'>{product?.totalActive || 0}</h6>
//                             </div> */}
//             <div className='d-flex justify-content-between'>
//                 <p className='dark-text'>Pending Request:</p>
//                 <h6 className='fw-bold'>{product?.pendingRequest || 0}</h6>
//             </div>
//             <div className='d-flex justify-content-between'>
//                 <p className='dark-text'>Total Revenue:</p>
//                 <h6 className='fw-bold'>€{product?.totalRevenue.toLocaleString() || 0}</h6>
//             </div>
//             <div className='d-flex justify-content-between border-top pt-3'>
//                 {/* <button className=''>Quick look</button> */}
//                 <button type='button' onClick={() => navigate(`/subscription?package=${product?.applicationName}&menu=SubscriptionSummary&duration=OneMonth`)} className='w-100'>Manage</button>
//             </div>
//         </div>
//     </div>)}
// </div> */}

